import { CompleteHocuspocusProviderConfiguration, ConstructableOutgoingMessage, HocuspocusProvider } from '@hocuspocus/provider'
import { type Encoder, createEncoder, toUint8Array, writeVarString, writeVarUint, writeVarUint8Array } from 'lib0/encoding'
import { encodeStateVector } from 'yjs'

function toUrlSafeBase64(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

class SseMessageSender {
  encoder: Encoder

  message: any

  constructor(Message: ConstructableOutgoingMessage, args: any = {}) {
    this.message = new Message()
    this.encoder = this.message.get(args)
  }

  create() {
    return toUint8Array(this.encoder)
  }

  send(sessionId: string): Promise<Response> {
    return fetch(`/api/tiptap-editor/session/${sessionId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: bytesToBase64(this.create()),
      }),
      credentials: 'include',
    })
  }
}

type BufferedOutboundMessage = {
  message: ConstructableOutgoingMessage
  args: any
}

export type SseSyncProviderConfiguration = Pick<CompleteHocuspocusProviderConfiguration, 'name' | 'document'>

function isRetryableStatus(status: number): boolean {
  return status === 425 || status === 429 || status >= 500
}

export class SseSyncProvider extends HocuspocusProvider {
  eventSource: EventSource | null = null

  connectionId: string | null = null

  private isDestroyed = false

  private isReconnecting = false

  private outboundBuffer: Array<BufferedOutboundMessage> = []

  constructor(configuration: SseSyncProviderConfiguration) {
    super({
      ...configuration,
      // Proxy all calls to the websocket provider as we're not using it for
      // syncing, but the HocuspocusProvider expects it to be there and will at
      // times make calls to it that we don't need. Passing a proxy allows us to
      // avoid having to rewrite the underlying provider whilst ensuring that
      // any calls to the websocket provider are effectively no-ops.
      websocketProvider: new Proxy({} as any, {
        get: () => () => {},
      }),
    })

    this.connectSse()
  }

  private connectSse(): void {
    if (this.eventSource !== null) {
      this.eventSource.close()
    }

    this.eventSource = new EventSource(`/api/tiptap-editor/document/${toUrlSafeBase64(this.configuration.name)}/sse`, {
      withCredentials: true,
    })

    this.eventSource.addEventListener('connectionId', (event: MessageEvent<string>) => {
      if (this.isDestroyed) {
        return
      }
      this.connectionId = event.data
      this.isReconnecting = false
      this.sendSyncStep1().then(() => {
        if (!this.isDestroyed) {
          this.flushOutboundBuffer()
        }
      })
    })

    this.eventSource.addEventListener('message', (event: MessageEvent<string>) => {
      if (this.isDestroyed) {
        return
      }
      this.onMessage({
        ...event,
        data: base64ToBytes(event.data).buffer as ArrayBuffer,
        target: null as any,
      })
    })
  }

  private reconnectSse(): void {
    if (this.isDestroyed || this.isReconnecting) {
      return
    }

    this.isReconnecting = true
    this.connectionId = null
    this.connectSse()
  }

  override destroy(): void {
    this.isDestroyed = true
    this.outboundBuffer = []
    if (this.eventSource !== null) {
      this.eventSource.close()
      this.eventSource = null
    }
    super.destroy()
  }

  override attach() {
    // no-op: Use SSE instead.
  }

  override detach(): void {
    // no-op: Use SSE instead.
  }

  override async send(message: ConstructableOutgoingMessage, args: any): Promise<void> {
    if (this.isDestroyed) {
      return
    }

    if (this.connectionId === null || this.isReconnecting) {
      this.outboundBuffer.push({ message, args })
      return
    }

    let response: Response
    try {
      response = await new SseMessageSender(message, args).send(this.connectionId)
    } catch {
      this.outboundBuffer.unshift({ message, args })
      this.reconnectSse()
      return
    }

    if (!response.ok && isRetryableStatus(response.status)) {
      this.outboundBuffer.unshift({ message, args })
      this.reconnectSse()
    }
  }

  private async flushOutboundBuffer(): Promise<void> {
    const messages = this.outboundBuffer
    this.outboundBuffer = []

    for (const bufferedMessage of messages) {
      await this.send(bufferedMessage.message, bufferedMessage.args)
    }
  }

  private async sendSyncStep1(): Promise<void> {
    if (this.isDestroyed || this.connectionId === null) {
      return
    }

    const encoder = createEncoder()
    writeVarString(encoder, this.configuration.name)
    writeVarUint(encoder, 0) // MessageType.Sync
    writeVarUint(encoder, 0) // messageYjsSyncStep1
    writeVarUint8Array(encoder, encodeStateVector(this.document))

    let response: Response
    try {
      response = await fetch(`/api/tiptap-editor/session/${this.connectionId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: bytesToBase64(toUint8Array(encoder)) }),
        credentials: 'include',
      })
    } catch {
      this.reconnectSse()
      return
    }

    if (!response.ok && isRetryableStatus(response.status)) {
      this.reconnectSse()
    }
  }
}
